import React, { createContext, useContext, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IAvailableCurrency } from 'services/BackendApi/types/AvailableCurrenciesResponse';
import { TCurrencyCode } from 'interfaces';
import { convertPrice, convertPriceCents } from 'utils/currencyConversion';
import { getCurrencySymbol } from 'utils/currency';
import { useAvailableCurrencies } from 'hooks/useAvailableCurrencies';

/**
 * Parse a price value that may be a formatted string (e.g., "13,882.05") or a number.
 * Strips commas before parsing to handle formatted price strings.
 */
const parsePrice = (value: string | number): number => {
  if (typeof value === 'number') return value;
  return parseFloat(value.replace(/,/g, ''));
};

interface ICurrencyConversionContext {
  selectedCurrencyData: IAvailableCurrency | null;
  sortedCurrencies: IAvailableCurrency[];
  convert: (amount: string | number) => number;
  convertCents: (amountCents: number) => number;
  displayCurrencyCode: TCurrencyCode;
  displayCurrencySymbol: string;
  isConverting: boolean;
  onCurrencyChange: (currencyCode: string | null) => void;
}

const CurrencyConversionContext = createContext<ICurrencyConversionContext | null>(null);

interface ICurrencyConversionProviderProps {
  hotelCurrency: TCurrencyCode;
  isEnabled: boolean;
  children: React.ReactNode;
}

export const CurrencyConversionProvider = ({
  hotelCurrency,
  isEnabled,
  children,
}: ICurrencyConversionProviderProps) => {
  const history = useHistory();
  const location = useLocation();
  const hasInitializedFromUrl = useRef(false);
  const [selectedCurrencyData, setSelectedCurrencyData] = useState<IAvailableCurrency | null>(null);

  const { sortedCurrencies } = useAvailableCurrencies(isEnabled);

  // Read currency from URL
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlCurrency = searchParams.get('currency') || null;

  // Initialize from URL once currencies are loaded
  useEffect(() => {
    if (hasInitializedFromUrl.current || !sortedCurrencies.length || !isEnabled) return;
    hasInitializedFromUrl.current = true;

    if (urlCurrency) {
      const currencyData = sortedCurrencies.find(c => c.to.code === urlCurrency) || null;
      setSelectedCurrencyData(currencyData);
    }
  }, [sortedCurrencies, urlCurrency, isEnabled]);

  const updateUrlCurrency = useCallback(
    (code: string | null) => {
      const newParams = new URLSearchParams(window.location.search);
      if (code) {
        newParams.set('currency', code);
      } else {
        newParams.delete('currency');
      }
      const newSearch = newParams.toString();
      const newUrl = newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
      history.replace(newUrl);
    },
    [history]
  );

  const onCurrencyChange = useCallback(
    (currencyCode: string | null) => {
      if (!currencyCode) {
        setSelectedCurrencyData(null);
        updateUrlCurrency(null);
        return;
      }
      const currencyData = sortedCurrencies.find(c => c.to.code === currencyCode) || null;
      setSelectedCurrencyData(currencyData);
      updateUrlCurrency(currencyCode);
    },
    [sortedCurrencies, updateUrlCurrency]
  );

  const isConverting = selectedCurrencyData !== null;
  const displayCurrencyCode = (isConverting ? selectedCurrencyData.to.code : hotelCurrency) as TCurrencyCode;
  const displayCurrencySymbol = getCurrencySymbol(displayCurrencyCode);

  const convert = useCallback(
    (amount: string | number): number => {
      const parsed = parsePrice(amount);
      if (!selectedCurrencyData) return parsed;
      return convertPrice(parsed, hotelCurrency, selectedCurrencyData);
    },
    [selectedCurrencyData, hotelCurrency]
  );

  const convertCents = useCallback(
    (amountCents: number): number => {
      if (!selectedCurrencyData) return amountCents;
      return convertPriceCents(amountCents, hotelCurrency, selectedCurrencyData);
    },
    [selectedCurrencyData, hotelCurrency]
  );

  const value = useMemo<ICurrencyConversionContext>(
    () => ({
      selectedCurrencyData,
      sortedCurrencies,
      convert,
      convertCents,
      displayCurrencyCode,
      displayCurrencySymbol,
      isConverting,
      onCurrencyChange,
    }),
    [selectedCurrencyData, sortedCurrencies, convert, convertCents, displayCurrencyCode, displayCurrencySymbol, isConverting, onCurrencyChange]
  );

  return <CurrencyConversionContext.Provider value={value}>{children}</CurrencyConversionContext.Provider>;
};

/**
 * Controlled-mode provider for contexts where each item manages its own currency
 * (e.g., basket items). No URL params — state is managed externally.
 */
interface IControlledCurrencyConversionProviderProps {
  hotelCurrency: TCurrencyCode;
  isEnabled: boolean;
  initialCurrencyCode?: string | null;
  onExternalCurrencyChange?: (currencyCode: string | null) => void;
  children: React.ReactNode;
}

export const ControlledCurrencyConversionProvider = ({
  hotelCurrency,
  isEnabled,
  initialCurrencyCode,
  onExternalCurrencyChange,
  children,
}: IControlledCurrencyConversionProviderProps) => {
  const [selectedCurrencyData, setSelectedCurrencyData] = useState<IAvailableCurrency | null>(null);
  const hasInitialized = useRef(false);

  const { sortedCurrencies } = useAvailableCurrencies(isEnabled);

  // Initialize from provided currency code once currencies are loaded
  useEffect(() => {
    if (hasInitialized.current || !sortedCurrencies.length || !isEnabled) return;
    hasInitialized.current = true;

    if (initialCurrencyCode) {
      const currencyData = sortedCurrencies.find(c => c.to.code === initialCurrencyCode) || null;
      setSelectedCurrencyData(currencyData);
    }
  }, [sortedCurrencies, initialCurrencyCode, isEnabled]);

  const onCurrencyChange = useCallback(
    (currencyCode: string | null) => {
      if (!currencyCode) {
        setSelectedCurrencyData(null);
        onExternalCurrencyChange?.(null);
        return;
      }
      const currencyData = sortedCurrencies.find(c => c.to.code === currencyCode) || null;
      setSelectedCurrencyData(currencyData);
      onExternalCurrencyChange?.(currencyCode);
    },
    [sortedCurrencies, onExternalCurrencyChange]
  );

  const isConverting = selectedCurrencyData !== null;
  const displayCurrencyCode = (isConverting ? selectedCurrencyData.to.code : hotelCurrency) as TCurrencyCode;
  const displayCurrencySymbol = getCurrencySymbol(displayCurrencyCode);

  const convert = useCallback(
    (amount: string | number): number => {
      const parsed = parsePrice(amount);
      if (!selectedCurrencyData) return parsed;
      return convertPrice(parsed, hotelCurrency, selectedCurrencyData);
    },
    [selectedCurrencyData, hotelCurrency]
  );

  const convertCents = useCallback(
    (amountCents: number): number => {
      if (!selectedCurrencyData) return amountCents;
      return convertPriceCents(amountCents, hotelCurrency, selectedCurrencyData);
    },
    [selectedCurrencyData, hotelCurrency]
  );

  const value = useMemo<ICurrencyConversionContext>(
    () => ({
      selectedCurrencyData,
      sortedCurrencies,
      convert,
      convertCents,
      displayCurrencyCode,
      displayCurrencySymbol,
      isConverting,
      onCurrencyChange,
    }),
    [selectedCurrencyData, sortedCurrencies, convert, convertCents, displayCurrencyCode, displayCurrencySymbol, isConverting, onCurrencyChange]
  );

  return <CurrencyConversionContext.Provider value={value}>{children}</CurrencyConversionContext.Provider>;
};

export const useCurrencyConversion = (): ICurrencyConversionContext => {
  const context = useContext(CurrencyConversionContext);
  if (!context) {
    return {
      selectedCurrencyData: null,
      sortedCurrencies: [],
      convert: (amount: string | number) => parsePrice(amount),
      convertCents: (amountCents: number) => amountCents,
      displayCurrencyCode: '' as TCurrencyCode,
      displayCurrencySymbol: '',
      isConverting: false,
      onCurrencyChange: () => {},
    };
  }
  return context;
};
